import {Glob as G } from "../../libs/Glob.mjs";

/**
 * Helper functions for AutoForm feature
 * @module autoFormFieldHelper
 */

/**
 * @type {AutoFormFieldHelperClass}
 * @implements {AutoFormFieldHelperObject}
 * @see {AutoFormFieldHelperObject}
 */
const autoFormFieldHelper = class {
	/**
	 * Build an autoFormFieldHelper object
	 * @param {AutoFormFieldHelperInit} options
	 */
	constructor(options) {
		Object.assign(this.#props, options);
	}

	/**
	 * Internal properties of autoFormFieldHelper object
	 * @private
	 * @type {object}
	 * @property { object } def
	 * @property { any } value
	 * @property {AutoFormHelperObject} form
	 * @property {AutoFormFieldHelperFieldStatus} status
	 * @property {function} [notify] Register notification of changes
	 */
	#props = { status: {} };

	/**
	 * Get field value
	 * @return {any}
	 */
	getValue() { return G.clone(this.#props.value); }

	/**
	 * Get field value for multiple groups
	 * @param {number} index
	 * @return {any}
	 */
	getMultiValue( index ) { return G.clone( (this.#props.value||[])[index] ); }

	/**
	 * Perform actual notification
	 * @private
	 * @function
	 * @return {void}
	 */
	#notify() {
		if ( typeof this.#props.notify === 'function') {
			this.#props.notify({
				value: G.clone(this.#props.value),
				hidden: this.hidden(),
				readonly: this.readonly(),
				missing: this.missing()
			});
		}
		if ( this.#props.form ) this.#props.form.notify(true);
	}

	/**
	 * Check if a required field is missing
	 * @return {boolean}
	 */
	missing() { return !!  (this.#props.def.required && ! this.hidden() && ! this.#props.value ); }

	/**
	 * Set/unset/trigger field notification function
	 * @param {function|true|undefined} func The function for notification, true to perform notification
	 * @return {void}
	 */
	notify( func ) {
		if ( typeof func === 'function') {
			this.#props.notify = func;
		} else if ( func ===  true ) {
			this.#notify();
		} else {
			delete this.#props.notify;
		}
	}

	/**
	 * Set field value
	 * @param {any} value Undefined to delete it
	 * @param {boolean} [isControl] This is a control field: don't notify, don't check dependencies
	 * @return {void}
	 */
	setValue( value, isControl=false ) {
		const ov = G.clone(this.#props.value);
		if (this.#props.def.key ) {
			if (this.#props.def.multiple) {
				if ( typeof value === "undefined" ) {
					this.#props.value = undefined;
				} else {
					this.#props.value = G.clone(Array.isArray(value) ? value : [ value ] );
				}
			} else {
				this.#props.value = G.clone(value);
			}
		}
		this.#props.form.setValue(this.#props.def.key, G.clone(this.#props.value));
		if ( !isControl && ov !== this.#props.value) this.#notify();
	}

	/**
	 * Set field value for multiple groups
	 * @param {number} index
	 * @param {any} value Undefined to delete it
	 * @param {boolean} [isControl] This is a control field: don't notify, don't check dependencies
	 * @return {void}
	 */
	setMultiValue( index, value, isControl=false ) {
		const v = G.clone(this.#props.value) || [];
		if ( typeof value === "undefined" ) {
			if ( typeof v[index] !== "undefined" ) v.splice( index, 1);
		} else {
			v[index] = G.clone(value);
		}
		this.#props.value = v;
		if (this.#props.def.key ) this.#props.form.setValue( this.#props.def.key, G.clone(v) );
		if ( !isControl ) this.#notify();
	}

	/**
	 * Get/set field status
	 * @param {AutoFormFieldHelperFieldStatus} [status]
	 * @return {AutoFormFieldHelperFieldStatus}
	 */
	status( status ){
		if ( typeof status === "object") {
			this.hidden( !! status.hidden );
			this.readonly( !! status.readonly );
		}
		return G.clone(status);
	}

	/**
	 * Get/set hidden status
	 * @param {boolean} [flag]
	 * @return {boolean}
	 */
	hidden( flag ) {
		if ( typeof flag !== "undefined" ) {
			const ov = !! this.#props.status.hidden;
			flag = !! flag;
			this.#props.status.hidden = flag;
			if ( this.#props.def.key ) {
				if ( flag ) {
					this.#props.form.setValue( this.#props.def.key )
				} else {
					this.#props.form.setValue( this.#props.def.key, G.clone(this.#props.value) );
				}
			}
			if ( ov !== flag ) this.#notify();
		}
		return !! this.#props.status.hidden;
	}

	/**
	 * Get/set readonly status
	 * @param {boolean} [flag]
	 * @return {boolean}
	 */
	readonly( flag ) {
		if ( typeof flag !== "undefined" ) {
			flag = !! flag;
			const ov = !! this.#props.status.readonly;
			this.#props.status.readonly = flag;
			if ( ov !== flag ) this.#notify();
		}
		return !! this.#props.status.readonly;
	}

}

export default autoFormFieldHelper;
